import type { SupportedRenderer } from "@/components/common/three-context/three-context";
import { assert } from "@faro-lotv/foundation";
import { PanoTile, RAD_TO_DEG } from "@faro-lotv/lotv";
import {
  Box3,
  Group,
  Light,
  Mesh,
  PerspectiveCamera,
  Vector3,
  WebGLRenderTarget,
  type Camera,
  type Object3D,
  type Scene,
} from "three";

/** Fraction of the box angular size added as margin around the framed region. */
const DEFAULT_PADDING_FRACTION = 0.08;

/** Largest dimension, in pixels, of the produced screenshot. */
const DEFAULT_MAX_DIMENSION_PX = 1024;

/**
 * Maximum field of view, in degrees, accepted for framing. A box that subtends
 * a wider angle (horizontally or vertically) cannot be captured as a whole
 * without extreme perspective distortion.
 */
const MAX_FOV_DEG = 150;

type BoundingBoxScreenshotOptions = {
  /**
   * Margin added around the framed box, as a fraction of the box angular size.
   * Defaults to {@link DEFAULT_PADDING_FRACTION}.
   */
  paddingFraction?: number;

  /**
   * Largest dimension, in pixels, of the produced image. The other dimension
   * is derived from the framed aspect ratio. Defaults to
   * {@link DEFAULT_MAX_DIMENSION_PX}.
   */
  maxDimensionPx?: number;
};

/**
 * Capture a PNG screenshot framing an entire bounding box mesh, independent of
 * the current on-screen view.
 *
 * The panorama is rendered from its center (where the user stands), so the box
 * always lies on the surrounding unit sphere. This function points a temporary
 * camera from that center towards the box and widens its field of view to fit
 * the whole box, then renders the scene off-screen. This guarantees the full
 * box is captured even when the user has aimed the live camera elsewhere.
 *
 * @param mesh The bounding box mesh to capture, already placed in the scene.
 * @param renderer The renderer used to render the off-screen frame.
 * @param scene The scene to render (panorama and bounding boxes).
 * @param camera The live camera, used as a template for the framing camera.
 * @param options Optional configuration for the capture.
 * @throws if the box cannot be framed or the image blob cannot be created.
 * @returns A promise resolving to a PNG `File` of the framed box.
 */
export async function createBoundingBoxScreenshot(
  mesh: Mesh,
  renderer: SupportedRenderer,
  scene: Scene,
  camera: Camera,
  options: BoundingBoxScreenshotOptions = {},
): Promise<File> {
  const {
    paddingFraction = DEFAULT_PADDING_FRACTION,
    maxDimensionPx = DEFAULT_MAX_DIMENSION_PX,
  } = options;

  const { camera: shotCamera, aspect } = frameCameraToBox(
    mesh,
    camera,
    paddingFraction,
  );
  const { width, height } = resolution(aspect, maxDimensionPx);

  // Multisampling smooths the bounding box edges, which are the focus of the
  // capture and would otherwise appear aliased.
  const target = new WebGLRenderTarget(width, height, { samples: 4 });
  const previousTarget = renderer.getRenderTarget();
  const pixels = new Uint8Array(width * height * 4);

  // The annotated box must be visible for the capture even if it was toggled
  // off in the live view; its visibility is restored afterwards.
  const wasMeshVisible = mesh.visible;
  mesh.visible = true;

  // Hide everything in the scene not necessary for the screenshot
  const visMap = new Map<Object3D, boolean>();
  scene.traverse((o) => {
    if (
      o === scene ||
      o instanceof Group ||
      o instanceof Light ||
      o instanceof PanoTile ||
      o === mesh ||
      // What the mesh is made of, e.g. its outline
      o.parent === mesh
    ) {
      return;
    }
    visMap.set(o, o.visible);
    o.visible = false;
  });

  renderer.setRenderTarget(target);
  renderer.render(scene, shotCamera);
  renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);

  // Restore visibility
  for (const [object, visibility] of visMap.entries()) {
    object.visible = visibility;
  }
  mesh.visible = wasMeshVisible;

  renderer.setRenderTarget(previousTarget);
  target.dispose();

  const imageBlob = await pixelsToPngBlob(pixels, width, height);
  if (!imageBlob) {
    throw new Error("Failed to create image blob");
  }

  return new File([imageBlob], `bounding-box_${Date.now()}.png`, {
    type: "image/png",
  });
}

type Framing = {
  /** A perspective camera oriented and zoomed to frame the whole box. */
  camera: PerspectiveCamera;
  /** The width / height aspect ratio of the framed region. */
  aspect: number;
};

/**
 * Build a perspective camera placed at the panorama center, looking at the box
 * and with a field of view wide enough to contain the entire box plus margin.
 *
 * @param mesh The mesh to frame.
 * @param camera The live camera, used as a template (near/far, up vector).
 * @param paddingFraction Margin added around the box, as a fraction of its size.
 * @returns The framing camera and aspect ratio.
 * @throws if the box cannot be framed (non-perspective camera, degenerate
 * bounds, or too wide an angle).
 */
function frameCameraToBox(
  mesh: Mesh,
  camera: Camera,
  paddingFraction: number,
): Framing {
  // Framing relies on a perspective field of view; other camera types are not
  // supported.
  assert(
    camera instanceof PerspectiveCamera,
    "Cannot frame the bounding box with a non-perspective camera",
  );

  mesh.updateWorldMatrix(true, false);
  const box = new Box3().setFromObject(mesh);
  assert(!box.isEmpty(), "Cannot frame an empty bounding box");

  // The mesh sits at the panorama center, so its world position is the point
  // the camera must look from for the panorama to render correctly.
  const center = mesh.getWorldPosition(new Vector3());
  const boxCenter = box.getCenter(new Vector3());

  const shotCamera = camera.clone();
  shotCamera.position.copy(center);
  shotCamera.up.copy(camera.up);
  shotCamera.lookAt(boxCenter);
  shotCamera.updateMatrixWorld(true);

  // Transform the box corners into the framing camera's view space (it looks
  // down -Z) to measure the half-angles the box subtends on each axis.
  const viewMatrix = shotCamera.matrixWorldInverse;
  const corner = new Vector3();
  let maxTanH = 0;
  let maxTanV = 0;

  for (const [cx, cy, cz] of BOX_CORNERS) {
    corner
      .set(
        cx ? box.max.x : box.min.x,
        cy ? box.max.y : box.min.y,
        cz ? box.max.z : box.min.z,
      )
      .applyMatrix4(viewMatrix);

    // A corner at or behind the camera plane cannot be framed by a perspective
    // projection.
    assert(
      corner.z < 0,
      "Cannot frame a bounding box that extends to or behind the camera",
    );

    const depth = -corner.z;
    maxTanH = Math.max(maxTanH, Math.abs(corner.x) / depth);
    maxTanV = Math.max(maxTanV, Math.abs(corner.y) / depth);
  }

  assert(
    maxTanH > 0 && maxTanV > 0,
    "Cannot frame a bounding box with no angular extent",
  );

  const padding = 1 + paddingFraction;
  const tanH = maxTanH * padding;
  const tanV = maxTanV * padding;

  const verticalFovDeg = 2 * Math.atan(tanV) * RAD_TO_DEG;
  const horizontalFovDeg = 2 * Math.atan(tanH) * RAD_TO_DEG;
  assert(
    verticalFovDeg <= MAX_FOV_DEG && horizontalFovDeg <= MAX_FOV_DEG,
    "Cannot frame a bounding box that subtends too wide a field of view",
  );

  const aspect = tanH / tanV;
  shotCamera.fov = verticalFovDeg;
  shotCamera.aspect = aspect;
  shotCamera.updateProjectionMatrix();

  return { camera: shotCamera, aspect };
}

type Resolution = {
  width: number;
  height: number;
};

/**
 * Derive an image resolution from an aspect ratio, capping the longer side to
 * the given maximum.
 *
 * @param aspect The width / height aspect ratio.
 * @param maxDimensionPx The maximum size of the longer side, in pixels.
 * @returns The image width and height in pixels.
 */
function resolution(aspect: number, maxDimensionPx: number): Resolution {
  if (aspect >= 1) {
    return {
      width: maxDimensionPx,
      height: Math.max(1, Math.round(maxDimensionPx / aspect)),
    };
  }
  return {
    width: Math.max(1, Math.round(maxDimensionPx * aspect)),
    height: maxDimensionPx,
  };
}

/**
 * Convert raw RGBA pixels read from a render target into a PNG blob.
 *
 * WebGL reads pixels bottom-up, so the rows are flipped vertically while being
 * copied into a top-down 2D canvas.
 *
 * @param pixels The RGBA pixel data, row-major and bottom-up.
 * @param width The image width in pixels.
 * @param height The image height in pixels.
 * @returns A promise resolving to a PNG blob, or `null` if encoding fails.
 */
function pixelsToPngBlob(
  pixels: Uint8Array,
  width: number,
  height: number,
): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Failed to acquire 2D context for screenshot");
  }

  const imageData = context.createImageData(width, height);
  const rowBytes = width * 4;
  for (let row = 0; row < height; row++) {
    const sourceStart = (height - 1 - row) * rowBytes;
    imageData.data.set(
      pixels.subarray(sourceStart, sourceStart + rowBytes),
      row * rowBytes,
    );
  }
  context.putImageData(imageData, 0, 0);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

/**
 * The eight corners of an axis-aligned box, expressed as flags selecting the
 * min (0) or max (1) value along each axis.
 */
const BOX_CORNERS: ReadonlyArray<readonly [number, number, number]> = [
  [0, 0, 0],
  [1, 0, 0],
  [0, 1, 0],
  [1, 1, 0],
  [0, 0, 1],
  [1, 0, 1],
  [0, 1, 1],
  [1, 1, 1],
];
